import { useState } from "react";
import { Link } from "react-router-dom";
import {
  BookOpen, PhoneCall, Users, Calculator, FileText, Send, CheckCircle2,
  AlertTriangle, ShieldCheck, Sparkles, Search, ArrowRight, Info, GitBranch,
  Mail, MessageSquare, Clock, Euro, Lock, Wrench,
} from "lucide-react";

/**
 * /help — In-app user manual for Atendimento and Gestor roles.
 * Static content — no API calls. Two tabs (Atendimento / Gestor) plus a
 * shared "Regras & FAQ" section. Uses the same Caring palette as the rest of
 * the settings pages (bege / dark grey / gold accents).
 */

const SECTIONS = {
  atendimento: [
    { id: "at-flow", title: "Visão geral do fluxo", icon: PhoneCall },
    { id: "at-new-lead", title: "1 · Criar nova qualificação", icon: Users },
    { id: "at-script", title: "2 · Preencher o Guião", icon: BookOpen },
    { id: "at-assign", title: "3 · Passar a lead ao Gestor", icon: ArrowRight },
    { id: "at-followup", title: "4 · Acompanhamento", icon: Clock },
  ],
  gestor: [
    { id: "gs-flow", title: "Visão geral do fluxo", icon: Calculator },
    { id: "gs-pickup", title: "1 · Receber a lead", icon: Users },
    { id: "gs-rt", title: "2 · Reunião Técnica (RT)", icon: MessageSquare },
    { id: "gs-estimate", title: "3 · Gerar / ajustar estimativa", icon: Calculator },
    { id: "gs-send-estimate", title: "4 · Enviar estimativa", icon: Send },
    { id: "gs-discount", title: "5 · Descontos e aprovação", icon: ShieldCheck },
    { id: "gs-proposal", title: "6 · Gerar proposta final", icon: FileText },
    { id: "gs-edit-proposal", title: "7 · Editar textos AI", icon: Sparkles },
    { id: "gs-send-proposal", title: "8 · Enviar proposta", icon: Send },
    { id: "gs-care-plan", title: "9 · Plano de Cuidados", icon: CheckCircle2 },
    { id: "gs-deposit", title: "10 · Caução e contrato", icon: Euro },
  ],
};

export default function UserManual() {
  const [role, setRole] = useState("atendimento");

  return (
    <div className="min-h-screen bg-muted/40 p-6 lg:p-10" data-testid="manual-root">
      <div className="max-w-6xl mx-auto space-y-6">
        <header>
          <div className="text-[10px] uppercase tracking-[0.25em] text-foreground font-semibold">Documentação</div>
          <h1 className="font-display text-3xl lg:text-4xl text-foreground mt-1">
            Manual do Utilizador
          </h1>
          <p className="text-sm text-muted-foreground mt-2 max-w-3xl leading-relaxed">
            Guia passo a passo do fluxo de gestão de clientes e operacional Caring, adaptado ao seu papel.
            Se ficar com dúvidas contacte o Administrador.
          </p>
        </header>

        {/* Role switcher */}
        <div className="inline-flex rounded-full border border-border bg-card overflow-hidden" data-testid="role-switcher">
          <button onClick={() => setRole("atendimento")}
                   className={`inline-flex items-center gap-2 text-xs px-5 py-2.5 ${role === "atendimento" ? "bg-foreground text-background" : "text-muted-foreground hover:bg-muted"}`}
                   data-testid="role-atendimento">
            <PhoneCall className="w-3.5 h-3.5" /> Atendimento
          </button>
          <button onClick={() => setRole("gestor")}
                   className={`inline-flex items-center gap-2 text-xs px-5 py-2.5 ${role === "gestor" ? "bg-foreground text-background" : "text-muted-foreground hover:bg-muted"}`}
                   data-testid="role-gestor">
            <Calculator className="w-3.5 h-3.5" /> Gestor
          </button>
          <Link to="#regras" onClick={() => {
                   const el = document.getElementById("regras");
                   if (el) el.scrollIntoView({ behavior: "smooth" });
                 }}
                 className="inline-flex items-center gap-2 text-xs px-5 py-2.5 text-foreground hover:bg-muted border-l border-border"
                 data-testid="role-regras">
            <ShieldCheck className="w-3.5 h-3.5" /> Regras & FAQ
          </Link>
        </div>

        <div className="grid grid-cols-1 lg:grid-cols-4 gap-6">
          {/* Table of contents */}
          <aside className="lg:col-span-1">
            <div className="sticky top-6 bg-card border border-border rounded-2xl p-4">
              <div className="text-[10px] uppercase tracking-[0.2em] font-semibold text-foreground mb-3">Índice</div>
              <nav className="space-y-1 text-sm">
                {SECTIONS[role].map((s) => (
                  <a key={s.id} href={`#${s.id}`}
                     className="flex items-start gap-2 py-1.5 px-2 rounded-md text-muted-foreground hover:bg-muted/40 hover:text-foreground"
                     data-testid={`toc-${s.id}`}>
                    <s.icon className="w-3.5 h-3.5 shrink-0 mt-0.5 text-foreground" />
                    <span className="text-[13px] leading-tight">{s.title}</span>
                  </a>
                ))}
              </nav>
            </div>
          </aside>

          {/* Content */}
          <main className="lg:col-span-3 space-y-8" data-testid="manual-content">
            {role === "atendimento" ? <AtendimentoContent /> : <GestorContent />}
            <RegrasFaq />
          </main>
        </div>
      </div>
    </div>
  );
}

/* ---------- Content components ---------- */

const Section = ({ id, icon: Icon, title, kicker, children }) => (
  <section id={id} className="bg-card border border-border rounded-2xl p-6 lg:p-8 scroll-mt-8" data-testid={`section-${id}`}>
    <div className="flex items-start gap-4 mb-4 pb-4 border-b border-border">
      <div className="w-11 h-11 rounded-xl bg-muted/40 border border-border flex items-center justify-center shrink-0">
        <Icon className="w-5 h-5 text-foreground" />
      </div>
      <div>
        {kicker && <div className="text-[10px] uppercase tracking-[0.2em] text-foreground font-semibold">{kicker}</div>}
        <h2 className="font-display text-2xl text-foreground leading-tight">{title}</h2>
      </div>
    </div>
    <div className="prose prose-sm max-w-none text-[#374151] space-y-4">{children}</div>
  </section>
);

const Step = ({ n, title, children }) => (
  <div className="flex gap-4">
    <div className="w-8 h-8 rounded-full bg-foreground text-background text-xs font-semibold flex items-center justify-center shrink-0">
      {n}
    </div>
    <div className="flex-1">
      <div className="font-semibold text-foreground mb-1">{title}</div>
      <div className="text-sm text-muted-foreground leading-relaxed space-y-2">{children}</div>
    </div>
  </div>
);

const Callout = ({ variant = "info", title, children }) => {
  const styles = {
    info: "bg-background border-[#BFDBFE] text-primary",
    warn: "bg-[hsl(var(--warning-bg))] border-[hsl(var(--warning))]/40 text-[#78350F]",
    ok:   "bg-[hsl(var(--success-bg))] border-[hsl(var(--success))]/40 text-[#065F46]",
    danger: "bg-[hsl(var(--danger-bg))] border-[hsl(var(--danger))]/30 text-[#7F1D1D]",
  };
  const Icon = { info: Info, warn: AlertTriangle, ok: CheckCircle2, danger: AlertTriangle }[variant];
  return (
    <div className={`rounded-lg border p-3 flex items-start gap-2 text-[13px] ${styles[variant]}`}>
      <Icon className="w-4 h-4 shrink-0 mt-0.5" />
      <div>
        {title && <div className="font-semibold mb-0.5">{title}</div>}
        <div className="leading-relaxed">{children}</div>
      </div>
    </div>
  );
};

const Kbd = ({ children }) => (
  <code className="bg-muted border border-border rounded px-1.5 py-0.5 text-[11px] font-mono text-foreground">{children}</code>
);


function AtendimentoContent() {
  return (
    <>
      <Section id="at-flow" icon={PhoneCall} kicker="Papel · Atendimento" title="Visão geral do fluxo">
        <p>O <b>Atendimento</b> é o primeiro ponto de contacto com a família. A sua missão é <b>qualificar a lead</b> — recolher toda a informação clínica, contextual e do cliente necessária para que o <b>Gestor de Cliente</b> possa propor um serviço adequado.</p>
        <div className="grid grid-cols-1 sm:grid-cols-4 gap-2 my-4">
          {["Nova lead (Zoho)", "Preencher Guião", "Passar ao Gestor", "Acompanhar"].map((s, i) => (
            <div key={i} className="rounded-lg bg-muted/40 border border-border px-3 py-2 text-xs text-foreground">
              <span className="text-foreground font-semibold mr-1">{i+1}.</span>{s}
            </div>
          ))}
        </div>
        <Callout title="O Atendimento nunca gera estimativas nem propostas">
          Todos os valores financeiros são responsabilidade do Gestor. O papel do Atendimento
          termina quando a qualificação está completa e uma Reunião Técnica está agendada.
        </Callout>
      </Section>

      <Section id="at-new-lead" icon={Users} kicker="Passo 1" title="Criar nova qualificação">
        <Step n="1" title="Entrar em «Nova Qualificação»">
          No menu lateral, clique em <Kbd>Nova Qualificação</Kbd>. Ou, se a lead veio de Zoho CRM, ela aparece
          automaticamente em <Kbd>Leads</Kbd> — clique para abrir.
        </Step>
        <Step n="2" title="Verificar duplicados">
          Antes de criar uma nova lead, pesquise pelo número de telefone ou email em <Kbd>Leads</Kbd>.
          Se encontrar uma lead activa da mesma pessoa, retome-a em vez de criar duplicado.
        </Step>
        <Step n="3" title="Selecionar o Gestor de destino">
          No topo da lead, escolha o <b>Gestor</b> que ficará responsável pela Reunião Técnica.
          Se não souber ainda, deixe em branco — o Administrador fará a atribuição.
        </Step>
      </Section>

      <Section id="at-script" icon={BookOpen} kicker="Passo 2" title="Preencher o Guião">
        <p>O Guião de Atendimento tem quatro secções obrigatórias:</p>
        <ol className="list-decimal list-inside space-y-2 pl-2">
          <li><b>Contacto</b> — quem é o <i>interlocutor</i> (quem está a falar consigo). Nome, telefone, email, relação com o beneficiário. Se o interlocutor <b>é</b> o beneficiário, marque a opção correspondente.</li>
          <li><b>Beneficiário(s)</b> — a pessoa que vai receber os cuidados. Idade, sexo, condições clínicas conhecidas, medicação, autonomia funcional. <b>Não invente diagnósticos</b> — anote só o que a família disser.</li>
          <li><b>Localização e logística</b> — distrito/concelho, acessibilidade da habitação, disponibilidade de família próxima.</li>
          <li><b>Serviço pretendido</b> — número de pessoas a apoiar, tipo de continuidade (<b>Continuado</b> vs <b>Pontual</b>), tipos de serviço (assistência diurna, nocturna, higiene, refeições, etc.). Se <b>Pontual</b>, defina data de <b>Início</b> e <b>Fim</b>.</li>
        </ol>
        <Callout variant="warn" title="Pontual precisa de datas">
          Serviços Pontuais só podem ser estimados se preencher <b>data de início</b> e <b>data de fim</b>. Sem isso o Gestor não conseguirá calcular o total de dias efetivos.
        </Callout>
        <Step n="4" title="Adicionar notas livres">
          No fim do guião há uma caixa <b>Notas para o Gestor</b>. Use-a para tudo o que a família disse que não coube nos campos estruturados —
          contexto emocional, urgência, expectativas de preço, restrições religiosas ou culturais, etc.
        </Step>
        <Step n="5" title="Guardar">
          Clique em <Kbd>Guardar</Kbd>. A lead fica em estado <Kbd>qualificada</Kbd> e é automaticamente atribuída ao Gestor selecionado.
        </Step>
      </Section>

      <Section id="at-assign" icon={ArrowRight} kicker="Passo 3" title="Passar a lead ao Gestor">
        <Step n="1" title="Agendar a Reunião Técnica (RT)">
          Antes de fechar a chamada, ofereça à família uma <b>Reunião Técnica gratuita</b> (visita presencial ao domicílio).
          Confirme uma data provisória. O Gestor confirmará depois.
        </Step>
        <Step n="2" title="Enviar SMS/Email de acompanhamento">
          No LeadDetail, clique em <Kbd>Enviar acknowledgment</Kbd> — a família recebe uma confirmação escrita
          da conversa e do próximo passo. Este email é enviado por Office 365 em nome do Atendimento.
        </Step>
        <Callout variant="ok">
          A partir daqui, a lead passa formalmente para o Gestor. Não gere estimativas nem propostas —
          essa é responsabilidade do Gestor após a RT.
        </Callout>
      </Section>

      <Section id="at-followup" icon={Clock} kicker="Passo 4" title="Acompanhamento subsequente">
        <p>Se a família ligar novamente com perguntas antes da RT:</p>
        <ul className="list-disc list-inside space-y-1 pl-2 text-sm">
          <li>Se for pergunta operacional (data, hora, morada): responda diretamente.</li>
          <li>Se for pergunta clínica ou de preço: <b>não estime valores</b>. Diga que o Gestor abordará esse ponto na RT.</li>
          <li>Actualize as notas da lead com o novo contexto.</li>
        </ul>
        <p>O <b>Relatório Diário</b> permite ver todas as leads que passou nas últimas 24h — use-o de manhã para o handover.</p>
      </Section>
    </>
  );
}


function GestorContent() {
  return (
    <>
      <Section id="gs-flow" icon={Calculator} kicker="Papel · Gestor" title="Visão geral do fluxo">
        <p>O <b>Gestor</b> é o ponto único de contacto do cliente do início ao fim. Recolhe a lead qualificada
        do Atendimento, realiza a Reunião Técnica, prepara a estimativa, envia a proposta e acompanha o serviço.</p>
        <div className="grid grid-cols-2 sm:grid-cols-5 gap-2 my-4">
          {["Recolher lead", "Fazer RT", "Estimativa", "Proposta", "Aceitação → Serviço"].map((s, i) => (
            <div key={i} className="rounded-lg bg-muted/40 border border-border px-3 py-2 text-xs text-foreground">
              <span className="text-foreground font-semibold mr-1">{i+1}.</span>{s}
            </div>
          ))}
        </div>
      </Section>

      <Section id="gs-pickup" icon={Users} kicker="Passo 1" title="Receber a lead">
        <p>As leads atribuídas a si aparecem em <Kbd>Leads</Kbd> filtradas pelo seu nome.
        Priorize as em estado <Kbd>qualificada</Kbd> (esperando RT).</p>
        <Step n="1" title="Ler o Guião completo">
          Cada lead tem toda a informação recolhida pelo Atendimento. Leia especialmente as <b>Notas para o Gestor</b>.
        </Step>
        <Step n="2" title="Verificar o Resumo Clínico">
          A IA gera automaticamente um resumo clínico consolidado. Se estiver desactualizado após alterações,
          clique em <Kbd>Regenerar resumo</Kbd>.
        </Step>
      </Section>

      <Section id="gs-rt" icon={MessageSquare} kicker="Passo 2" title="Reunião Técnica (RT)">
        <Callout variant="warn" title="Nenhuma estimativa é enviada ao cliente sem uma decisão de RT registada">
          O sistema bloqueia o envio de estimativas se não houver uma decisão de RT — aceite ou recusada.
        </Callout>
        <Step n="1" title="Agendar a RT">
          No LeadDetail há um botão <Kbd>Agendar RT</Kbd>. Escolha data + hora — o sistema envia convite via Zoho Bookings.
        </Step>
        <Step n="2" title="Realizar a visita">
          Após a visita presencial, volte ao LeadDetail e clique em <Kbd>Decisão da RT</Kbd>.
        </Step>
        <Step n="3" title="Registar a decisão">
          O diálogo pede: <b>Aceite</b> ou <b>Recusada</b>. Se recusada, motivo obrigatório.
          <ul className="list-disc list-inside space-y-1 mt-2">
            <li><b>Aceite</b> → cria tarefa de follow-up no Zoho CRM e desbloqueia envio da estimativa.</li>
            <li><b>Recusada</b> → lead vai para <Kbd>perdida</Kbd> com o motivo.</li>
          </ul>
        </Step>
      </Section>

      <Section id="gs-estimate" icon={Calculator} kicker="Passo 3" title="Gerar / ajustar a estimativa">
        <Step n="1" title="Gerar automaticamente">
          Clique em <Kbd>Gerar estimativa</Kbd>. A IA aplica a tabela de preços vigente e a informação clínica para
          propor 2-3 opções (uma primária + alternativas), com cálculo automático de horas, dias e valores.
        </Step>
        <Step n="2" title="Rever cada opção">
          Pode editar preço/hora, dias/semana, horas/dia, taxa extra ou notas em qualquer linha.
          O cálculo total (mensal ou pontual) actualiza-se em tempo real.
        </Step>
        <Step n="3" title="Definir a Opção Primária">
          A opção primária é a "sugestão Caring" — a que aparece com destaque na estimativa que o cliente vê.
        </Step>
        <Step n="4" title="Catálogo (atalho)">
          No lado direito de cada linha há um botão <Kbd>Catálogo</Kbd> — abre um popover com valores
          recomendados da tabela de preços. Clique para aplicar automaticamente.
        </Step>
        <Step n="5" title="Confirmar valores após RT">
          Depois da RT clique em <Kbd>Valores confirmados</Kbd> se a estimativa permanece igual, ou <Kbd>Alterar (justificar)</Kbd> se precisa ajustar.
          <br/>Se ajustar, o sistema pede <b>motivo</b> obrigatório.
        </Step>
        <Callout variant="warn" title="Reduções precisam de aprovação">
          Se o novo valor for <b>inferior</b> ao valor original enviado ao cliente, o pedido é encaminhado
          automaticamente para <b>Rui Pinto ou Daniela Monteiro</b>. Veja o passo 5 abaixo.
        </Callout>
      </Section>

      <Section id="gs-send-estimate" icon={Send} kicker="Passo 4" title="Enviar estimativa">
        <Step n="1" title="Rever o email">
          O botão <Kbd>Enviar estimativa</Kbd> abre um diálogo com o email pré-preenchido pela IA.
          Pode editar assunto, corpo e anexos antes de enviar.
        </Step>
        <Step n="2" title="Confirmar envio">
          O sistema:
          <ul className="list-disc list-inside space-y-1 mt-1">
            <li>Envia por Office 365 em seu nome (com CC ao Atendimento).</li>
            <li>Gera um <b>link público</b> para o cliente (<Kbd>/estimate/&lt;token&gt;</Kbd>) que ele pode partilhar.</li>
            <li>Cria uma tarefa de <b>seguimento 72h</b> no Zoho CRM.</li>
          </ul>
        </Step>
      </Section>

      <Section id="gs-discount" icon={ShieldCheck} kicker="Passo 5" title="Descontos e aprovação">
        <Callout variant="warn">
          <b>Só reduções precisam de aprovação.</b> Aumentos são aplicados de imediato.
        </Callout>
        <Step n="1" title="Justificar a redução">
          Quando reduz o valor, escreva um motivo claro (ex: <i>"cliente aceitou continuidade se preço baixar 10%"</i>).
        </Step>
        <Step n="2" title="Aprovação automática">
          O sistema envia email a <b>Rui Pinto</b> e <b>Daniela Monteiro</b> com um link seguro.
          <br/>Basta uma aprovação de qualquer um deles para o desconto ser aplicado.
        </Step>
        <Step n="3" title="Acompanhar o pedido">
          No LeadDetail aparece um banner âmbar <Kbd>Redução aguarda aprovação</Kbd> com valor antes/depois, delta e prazo.
        </Step>
        <Step n="4" title="Resultado">
          <ul className="list-disc list-inside space-y-1">
            <li><b>Aprovado</b> → recebe email + o desconto é aplicado automaticamente.</li>
            <li><b>Recusado</b> → recebe email com o motivo. Pode submeter novo pedido.</li>
            <li>Pedido expira em <b>48h</b>.</li>
          </ul>
        </Step>
      </Section>

      <Section id="gs-proposal" icon={FileText} kicker="Passo 6" title="Gerar a proposta final">
        <p>A proposta é o documento premium que substitui a estimativa. Só pode ser gerada depois de:</p>
        <ul className="list-disc list-inside space-y-1">
          <li>RT aceite</li>
          <li>Estimativa confirmada (valores estáveis após RT)</li>
        </ul>
        <Step n="1" title="Clicar «Gerar Proposta»">
          A IA prepara em ~30 segundos: mensagem pessoal, resumo executivo, compreensão das necessidades,
          vantagens Caring, cronograma, FAQ, testemunhos e condições comerciais.
        </Step>
        <Step n="2" title="Escolher o template">
          Em <Kbd>Layout Proposta (blocos)</Kbd> (menu Admin) pode gerir templates diferentes (Standard, Sénior Independente, Casal, etc.).
          O template escolhido no lead define quais blocos aparecem.
        </Step>
      </Section>

      <Section id="gs-edit-proposal" icon={Sparkles} kicker="Passo 7" title="Editar textos AI da proposta">
        <p>A IA gera textos institucionais mas ocasionalmente escreve algo que precisa de ser ajustado ao contexto.</p>
        <Step n="1" title="Abrir o editor">
          No LeadDetail, secção Proposta, clique em <Kbd>Editar textos</Kbd>. Abre um modal com todos os campos AI editáveis:
          mensagem pessoal, resumo executivo, compreensão das necessidades, serviço recomendado, vantagens, condições comerciais, notas adicionais.
        </Step>
        <Step n="2" title="Melhorar com IA">
          Cada campo tem um botão <Kbd>Melhorar com IA</Kbd> que reescreve o texto respeitando as instruções
          globais (definidas em <Kbd>Instruções à IA</Kbd> pelo Administrador).
        </Step>
        <Step n="3" title="Guardar">
          As alterações refletem-se de imediato no PDF e na página pública. O sistema regista quem editou e quando.
        </Step>
        <Callout>
          Se preferir uma reescrita completa, clique em <Kbd>Regerar proposta</Kbd> — a IA descarta tudo e gera novos textos usando as instruções mais recentes.
        </Callout>
      </Section>

      <Section id="gs-send-proposal" icon={Send} kicker="Passo 8" title="Enviar a proposta">
        <Step n="1" title="Descarregar / rever o PDF">
          Clique em <Kbd>Descarregar PDF</Kbd> antes de enviar para uma última leitura visual. Aproveite para
          confirmar o layout do PDF e valores.
        </Step>
        <Step n="2" title="Enviar por email">
          <Kbd>Enviar proposta</Kbd> envia PDF + link público <Kbd>/proposal/&lt;token&gt;</Kbd> onde o cliente pode aceitar digitalmente.
        </Step>
        <Step n="3" title="Aceitação digital">
          Quando o cliente clica em <b>Aceitar</b> na página pública, a lead avança para <Kbd>aceite</Kbd> e recebe
          automaticamente notificação (email + tarefa Zoho + canal ClickUp).
        </Step>
      </Section>

      <Section id="gs-care-plan" icon={CheckCircle2} kicker="Passo 9" title="Plano de Cuidados">
        <p>Após aceitação, é gerado automaticamente um <b>Plano de Cuidados provisório</b>, disponível ao cliente em <Kbd>/care-plan/&lt;token&gt;</Kbd>.</p>
        <Step n="1" title="Validação técnica (Direção Técnica)">
          A Direção Técnica agenda uma visita ao domicílio para validar objetivos, prioridades e atividades planeadas.
        </Step>
        <Step n="2" title="Após validação">
          A Direção Técnica aprova a versão final. O banner "PROVISÓRIO" no plano público é substituído por "Documento validado".
        </Step>
        <div className="mt-5 rounded-lg border border-border bg-muted/40 p-4 flex items-start gap-3" data-testid="cp-guide-cta">
          <BookOpen className="w-5 h-5 text-muted-foreground shrink-0 mt-0.5" strokeWidth={1.6} />
          <div className="flex-1">
            <div className="font-heading text-sm text-foreground mb-1">Guião prático — como construir um bom Plano de Cuidados</div>
            <p className="text-[13px] text-muted-foreground leading-relaxed mb-2">
              Ver o guião detalhado com os 28 blocos, exemplos bons vs fracos, integração com a IA
              da RT e o Manual do Cuidador, e checklist final antes das aprovações.
            </p>
            <Link to="/help/care-plan-guide"
                  data-testid="cp-guide-link"
                  className="inline-flex items-center gap-1.5 text-[13px] text-primary hover:underline font-medium">
              Abrir guião completo <ArrowRight className="w-3.5 h-3.5" />
            </Link>
          </div>
        </div>
      </Section>

      <Section id="gs-deposit" icon={Euro} kicker="Passo 10" title="Caução e contrato">
        <Step n="1" title="Definir a caução">
          Na secção "Caução" defina: valor total, arranque (recruitment fee), remanescente e data de início.
        </Step>
        <Step n="2" title="Enviar link de pagamento">
          O cliente recebe email com link para pagar. Um cron envia <b>lembrete às 72h</b> se não pagar.
        </Step>
        <Step n="3" title="Confirmar depósito">
          Ao registar o depósito no LeadDetail, a lead passa a <Kbd>ativa</Kbd> e a equipa operacional começa o serviço.
        </Step>
      </Section>
    </>
  );
}


function RegrasFaq() {
  return (
    <section id="regras" className="bg-foreground text-background border border-[#0F172A] rounded-2xl p-6 lg:p-8 scroll-mt-8" data-testid="section-regras">
      <div className="flex items-start gap-4 mb-4">
        <div className="w-11 h-11 rounded-xl bg-primary/20 border border-primary/40 flex items-center justify-center shrink-0">
          <ShieldCheck className="w-5 h-5 text-primary" />
        </div>
        <div>
          <div className="text-[10px] uppercase tracking-[0.2em] text-primary font-semibold">Comum a todos os papéis</div>
          <h2 className="font-display text-2xl leading-tight">Regras críticas e FAQ</h2>
        </div>
      </div>
      <div className="space-y-4 text-sm">
        <div className="rounded-lg bg-foreground border border-white/5 p-4">
          <div className="text-[10px] uppercase tracking-wider text-primary font-semibold mb-2 flex items-center gap-1.5"><Lock className="w-3 h-3"/> Segurança</div>
          <ul className="list-disc list-inside space-y-1 text-primary-foreground/80">
            <li>Nunca partilhe passwords. Cada utilizador tem a sua.</li>
            <li>Todos os emails ao cliente são enviados por Office 365 em nome do utilizador — surge no seu histórico de "Enviados".</li>
            <li>Links públicos (<Kbd>/estimate/&lt;token&gt;</Kbd>, <Kbd>/proposal/&lt;token&gt;</Kbd>, <Kbd>/care-plan/&lt;token&gt;</Kbd>) não requerem autenticação — apenas o token secreto. Não republique-os fora do canal com o cliente.</li>
          </ul>
        </div>

        <div className="rounded-lg bg-foreground border border-white/5 p-4">
          <div className="text-[10px] uppercase tracking-wider text-primary font-semibold mb-2 flex items-center gap-1.5"><Euro className="w-3 h-3"/> Fiscalidade</div>
          <ul className="list-disc list-inside space-y-1 text-primary-foreground/80">
            <li>Os serviços Caring são <b>isentos de IVA</b>. Nunca comunique qualquer taxa aplicável.</li>
            <li>Nunca mencione seguros, responsabilidade civil ou acidentes de trabalho em comunicações ao cliente.</li>
            <li>Se a IA gerar algo destas categorias, é filtrado automaticamente — mas verifique sempre antes de enviar.</li>
          </ul>
        </div>

        <div className="rounded-lg bg-foreground border border-white/5 p-4">
          <div className="text-[10px] uppercase tracking-wider text-primary font-semibold mb-2 flex items-center gap-1.5"><MessageSquare className="w-3 h-3"/> Tom institucional</div>
          <ul className="list-disc list-inside space-y-1 text-primary-foreground/80">
            <li>Evite linguagem emotiva ou familiar (<i>"a vossa família"</i>, <i>"quem tanto se doou"</i>, <i>"com carinho"</i>).</li>
            <li>Prefira construções impessoais: <i>"a Caring propõe"</i>, <i>"a presente proposta prevê"</i>.</li>
            <li>Refira sempre o beneficiário pelo <b>nome próprio</b>, sem inferir relações familiares.</li>
          </ul>
        </div>

        <div className="rounded-lg bg-foreground border border-white/5 p-4">
          <div className="text-[10px] uppercase tracking-wider text-primary font-semibold mb-2 flex items-center gap-1.5"><ShieldCheck className="w-3 h-3"/> Approval gates</div>
          <ul className="list-disc list-inside space-y-1 text-primary-foreground/80">
            <li><b>Redução de estimativa</b> → aprovação de Rui Pinto ou Daniela Monteiro.</li>
            <li><b>Envio de estimativa</b> → só depois de decisão da RT registada.</li>
            <li><b>Geração de proposta</b> → só depois de RT aceite e estimativa confirmada.</li>
          </ul>
        </div>

        <div className="rounded-lg bg-foreground border border-white/5 p-4">
          <div className="text-[10px] uppercase tracking-wider text-primary font-semibold mb-2 flex items-center gap-1.5"><Wrench className="w-3 h-3"/> Problemas comuns</div>
          <dl className="space-y-3 text-primary-foreground/80">
            <div>
              <dt className="font-semibold text-primary-foreground">O email não chegou ao cliente</dt>
              <dd className="text-primary-foreground/70">Verifique a caixa "Enviados" no Office 365. Se lá estiver, confirme SPAM com o cliente.</dd>
            </div>
            <div>
              <dt className="font-semibold text-primary-foreground">"Não posso enviar estimativa"</dt>
              <dd className="text-primary-foreground/70">A RT não está registada. Volte ao LeadDetail e clique em <Kbd>Decisão da RT</Kbd>.</dd>
            </div>
            <div>
              <dt className="font-semibold text-primary-foreground">A IA escreve algo incorreto</dt>
              <dd className="text-primary-foreground/70">Peça ao Administrador para adicionar uma regra em <Kbd>Instruções à IA</Kbd> — ficará memorizada para todas as gerações futuras.</dd>
            </div>
            <div>
              <dt className="font-semibold text-primary-foreground">Preciso trocar o Gestor de uma lead</dt>
              <dd className="text-primary-foreground/70">Só o Administrador pode reatribuir. Envie o pedido via SAC.</dd>
            </div>
          </dl>
        </div>

        <div className="text-xs text-primary-foreground/50 text-center pt-4">
          Este manual reflete a versão actual do sistema. Para sugerir melhorias, contacte o Administrador.
        </div>
      </div>
    </section>
  );
}
